import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { COMPANY_NAME, APP_NAME_FULL, APP_VERSION } from '../constants';

// Always use the correct API Microsoft login URL (avoid /accounts/microsoft/login/ which 500s)
const getMicrosoftLoginUrl = () => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  if (base.includes('students-api.hasc.net')) {
    return 'https://students-api.hasc.net/api/auth/microsoft/login/';
  }
  return base.replace(/\/api\/?$/, '') + '/api/auth/microsoft/login/';
};

const Login = () => {
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const qError = searchParams.get('error');
    if (qError) {
      const messages = {
        invalid_state: 'Invalid state. Please try signing in again.',
        token_exchange_failed: 'Token exchange failed. Please try again.',
        no_id_token: 'No identity token received.',
        invalid_id_token: 'Invalid identity token.',
        wrong_tenant: 'This account is not from the allowed organization.',
        no_email: 'No email in account.',
        no_group: 'Your account has not been assigned access to this system. Please contact your administrator.',
      };
      setError(messages[qError] || qError);
    }
    if (searchParams.get('reset') === 'success') {
      setSuccessMsg('Your password has been reset. Sign in with your new password.');
    }
  }, [searchParams]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Logo – transparent PNG */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
              backgroundColor: 'transparent',
              '& img': {
                display: 'block',
                maxHeight: 120,
                maxWidth: '100%',
                objectFit: 'contain',
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                verticalAlign: 'middle',
              },
            }}
          >
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt={COMPANY_NAME}
              onError={(e) => {
                const el = e.target;
                if (el.dataset.triedSvg) el.style.display = 'none';
                else { el.dataset.triedSvg = '1'; el.src = process.env.PUBLIC_URL + '/logo.svg'; }
              }}
              style={{ background: 'transparent' }}
            />
          </Box>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h6" align="center" gutterBottom sx={{ fontWeight: 600 }}>
              {COMPANY_NAME}
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mt: -0.5, mb: 1, fontWeight: 500 }}>
              {APP_NAME_FULL}
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              HIPAA and FERPA Compliant
            </Typography>

            {successMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMsg}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 1, mb: 2 }}
              href={getMicrosoftLoginUrl()}
            >
              Sign in with Microsoft
            </Button>
          </Paper>

          <Typography
            component="footer"
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, textAlign: 'center', display: 'block' }}
          >
            © {new Date().getFullYear()} HASC · School Year Management System · v{APP_VERSION}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
